import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage, getApiErrorCode } from '../utils/apiError';
import { isSafeInternalDeeplink } from '../utils/deeplink';
import { isKnownRoutePath } from '../utils/knownRoutes';
import { findUrlInText } from '../utils/urlDetection';
import ConfirmDialog from '../components/admin/ConfirmDialog';
import { Search, Eye, X as XIcon, CheckCircle, RefreshCw, AlertTriangle, Trash2, XCircle, UserPlus2, ChevronLeft, ChevronRight, Filter, Download, Mail, Send, Users, Link2, Loader2, ShieldCheck, Megaphone, BellRing, Smartphone, Info } from 'lucide-react';
import PageHeader from '../components/admin/PageHeader';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import PushSelfTestModal from '../components/admin/PushSelfTestModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "Filtreye uyan tümünü seç" + dış kanal (email/whatsapp) kombinasyonunda
// bulk_notify backend'de SIRALI (sequential await) çalışır — büyük hedef
// kitlelerde tek HTTP isteği dakikalarca sürüp timeout'a çarpabilir (bkz.
// backend/routes/admin_bulk.py::_bulk_execute docstring). Bu eşiğin üzerinde
// dış kanal seçiliyse admin'e ek bir "anladım" onayı zorunlu tutulur.
const BULK_EXTERNAL_RISK_THRESHOLD = 300;

// "Filtreye uyan tümünü seç" backend'i (BulkAdminUserFilters) yalnızca
// role+search destekliyor — bu sayfanın gelişmiş filtreleri (şehir/meslek/
// durum/tarih/kurs/...) bu moda YANSIMAZ (reviewer bulgusu: küçük punto
// uyarısı yeterli değil, admin gördüğünden çok daha geniş bir kitleye
// gönderebilir). `sort` kasıtlı olarak HARİÇ — hedef kitleyi etkilemez.
const BULK_IGNORED_FILTER_LABELS = {
  city: { tr: 'Şehir', en: 'City' },
  meslek: { tr: 'Meslek', en: 'Occupation' },
  status: { tr: 'Durum', en: 'Status' },
  date_from: { tr: 'Başlangıç tarihi', en: 'Start date' },
  date_to: { tr: 'Bitiş tarihi', en: 'End date' },
  course_id: { tr: 'Kurs', en: 'Course' },
  enroll_active: { tr: 'Aktif kayıt', en: 'Active enrollment' },
  exam_taken: { tr: 'Sınav durumu', en: 'Exam taken' },
  graduate: { tr: 'Mezuniyet', en: 'Graduate' },
  invoice_status: { tr: 'Borç Durumu', en: 'Debt Status' },
  missing_docs: { tr: 'Eksik belge', en: 'Missing docs' },
};

const statusConfig = {
  active: { color: 'bg-green-100 text-green-600', label_tr: 'Aktif', label: 'Active' },
  frozen: { color: 'bg-blue-100 text-blue-600', label_tr: 'Dondurulmuş', label: 'Frozen' },
  suspended: { color: 'bg-red-100 text-red-600', label_tr: 'Askıda', label: 'Suspended' },
  graduated: { color: 'bg-purple-100 text-purple-600', label_tr: 'Mezun', label: 'Graduated' },
};

export default function AdminStudentOps() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [courses, setCourses] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(null);
  const [studentPage, setStudentPage] = useState(0);
  const navigate = useNavigate();
  const STUDENT_LIMIT = 10;

  // Faz 1: cok kriterli filtre + toplu islem
  const EMPTY_FILTERS = { city: '', meslek: '', status: '', date_from: '', date_to: '', course_id: '', enroll_active: '', exam_taken: '', graduate: '', invoice_status: '', missing_docs: '', sort: '' };
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [bulkMsg, setBulkMsg] = useState(null);
  const [sendingMsg, setSendingMsg] = useState(false);
  // Serbest metin URL onay uyarısı (security denetimi, 2026-08-04):
  // isSafeInternalDeeplink SADECE ayrı `deeplink` alanını korur — bu
  // başlık/mesaj metnine yazılan bir bağlantı (ör. "http://evil.com")
  // guard'dan HİÇ GEÇMEZ ve bildirim artık gerçek push olarak cihaza
  // gittiğinden kilit ekranında phishing riski taşır. Kullanıcı kararı:
  // ENGELLEME, sadece ek onay iste — bkz. utils/urlDetection.js.
  const [bulkUrlConfirmOpen, setBulkUrlConfirmOpen] = useState(false);
  // "Seçili öğrenciler" (mevcut checkbox seçimi) vs "Filtreye uyan tüm öğrenciler"
  // (select_all_matching — backend'de sadece role+search destekleniyor, bkz.
  // BulkAdminUserFilters; bu yüzden bu modda SADECE arama kutusu + role=student
  // dikkate alınır, sayfadaki diğer gelişmiş filtreler [şehir/meslek/...] YOK sayılır).
  const [bulkTargetMode, setBulkTargetMode] = useState('selected');
  // count: ham filtre sayısı (acknowledged_count için — DEĞİŞTİRME, bkz.
  // sendBulkMessage). effectiveCount/protectedCount: bkz. preview-count effect
  // yorumu — arayüzde gösterilen ve risk eşiğinde kullanılan effectiveCount'tur.
  const [bulkPreview, setBulkPreview] = useState({ count: null, effectiveCount: null, protectedCount: 0, loading: false, error: false, exceedsLimit: false });
  const [bulkRiskAck, setBulkRiskAck] = useState(false);
  // İleti türü — KVKK/6563 sayılı Kanun uyumu (bkz. backend/routes/admin_bulk.py
  // ::BulkNotifyPayload.message_category "service"|"commercial", varsayılan
  // "service" — commit 51cf61f, doğrulandı). "service": dış kanallar
  // kullanıcı opt-out ETMEDİĞİ sürece gider. "commercial": dış kanallar
  // sadece açık rızası (opt-in) olan alıcılara gider — rızası olmayanlar
  // backend tarafında elenir (bkz. sendBulkMessage'daki skipped_no_consent).
  const [bulkMessageCategory, setBulkMessageCategory] = useState('service');
  // Push fan-out teşhis paneli (2026-08-04 görev talimatı — "admin bulk push
  // atmıyordu" bug fix'inin bir parçası). Toplu bildirim modalından bağımsız
  // — admin göndermeden önce zinciri kendi hesabında test edebilir.
  const [pushTestOpen, setPushTestOpen] = useState(false);
  // Son toplu bildirim gönderiminin push telemetrisi (backend/routes/
  // admin_bulk.py::bulk_notify — push_eligible_users/push_skipped_*/
  // push_tokens_sent/push_tokens_failed, hepsi eski response'larda YOK
  // olabilir, bkz. sendBulkMessage). Sadece görüntüleme amaçlı, ayrı bir
  // "sonuç ekranı" değil — bulkMsg modalı kapandıktan sonra liste üstünde
  // gösterilen küçük bir kart.
  const [bulkPushTelemetry, setBulkPushTelemetry] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [capped, setCapped] = useState(false);
  const setF = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setStudentPage(0); };
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearch(''); setStudentPage(0); };
  const setGroup = (patch) => { setFilters({ ...EMPTY_FILTERS, ...patch }); setStudentPage(0); };
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const buildParams = () => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p;
  };

  const fetchStudents = async () => {
    setLoadingList(true);
    try {
      const p = buildParams();
      p.set('page', String(studentPage + 1));
      p.set('page_size', String(STUDENT_LIMIT));
      const r = await axios.get(`${API}/admin/student-ops?${p.toString()}`, { headers: authHeaders() });
      setStudents(r.data.students || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      setCapped(!!r.data.capped);
    } catch { /* liste yüklenemedi — finally durumu sıfırlar */ } finally { setLoadingList(false); }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const qs = buildParams().toString();
      const res = await axios.get(`${API}/admin/students/export${qs ? `?${qs}` : ''}`, { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `ogrenciler-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setExporting(false); }
  };

  // "Filtreye uyan tümünü seç" modunda debounce'lu preview-count — sadece modal
  // açıkken ve bu mod seçiliyken çalışır (bkz. POST /admin/bulk/preview-count).
  //
  // count vs. protected_count/effective_count (coordinator eşitleme turu —
  // backend admin/superadmin hedeflerini artık HER ZAMAN atlıyor, bkz.
  // backend/routes/admin_bulk.py::bulk_preview_count docstring):
  //   - count: ham filtre eşleşmesi (admin/superadmin DAHİL) — SADECE
  //     acknowledged_count TOCTOU sözleşmesi için saklanır, kullanıcıya
  //     GÖSTERİLMEZ.
  //   - effective_count: count - protected_count, yani gönderimin GERÇEKTEN
  //     ulaşacağı kişi sayısı — arayüzde gösterilen VE risk eşiğinde
  //     kullanılan sayı budur (bkz. bulkTargetCount).
  //   - protected_count: filtreye uyan ama admin/superadmin olduğu için
  //     atlanacak kayıt sayısı — protected_count > 0 ise kullanıcıya ayrıca
  //     bildirilir ("N yönetici hesabı hariç tutuldu").
  // Eski backend sürümüyle uyumluluk: effective_count/protected_count
  // gelmezse (?? count / ?? 0) çökmeden eski davranışa (ham count) düşer.
  useEffect(() => {
    if (!bulkMsg || bulkTargetMode !== 'all_matching') return undefined;
    let cancelled = false;
    setBulkPreview(p => ({ ...p, loading: true, error: false }));
    const handle = setTimeout(async () => {
      try {
        const r = await axios.post(
          `${API}/admin/bulk/preview-count`,
          { entity: 'users', filters: { role: 'student', search: search || undefined } },
          { headers: authHeaders() }
        );
        if (!cancelled) {
          const data = r.data || {};
          setBulkPreview({
            count: data.count,
            effectiveCount: data.effective_count ?? data.count,
            protectedCount: data.protected_count ?? 0,
            loading: false,
            error: false,
            exceedsLimit: !!data.exceeds_limit,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setBulkPreview(p => ({ ...p, loading: false, error: true }));
          // Sessiz kalma — 422 (ör. boş filtre) dahil her hata gerçek
          // backend mesajıyla toast'a düşer (bkz. mevcut akışta role her
          // zaman 'student' gönderildiğinden bu normalde tetiklenmez, ama
          // network/403/5xx gibi diğer hatalar için de anlamlı geri bildirim
          // gerekir).
          toast.error(getApiErrorMessage(err, lang) || (lang === 'tr' ? 'Hedef kitle sayısı alınamadı' : 'Could not fetch target count'));
        }
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkMsg, bulkTargetMode, search]);

  // "Filtreye uyan tümünü seç" modu backend'de sadece role+search destekliyor
  // — bu sayfanın gelişmiş filtreleri (BULK_IGNORED_FILTER_LABELS) bu moda
  // YANSIMAZ. Reviewer bulgusu: bu durumda mod tamamen DEVRE DIŞI bırakılır
  // (admin önce "Temizle" ile filtreleri kaldırmalı) — küçük punto uyarısı
  // yeterli değildi.
  const bulkActiveAdvancedFilterKeys = Object.entries(filters)
    .filter(([k, v]) => k !== 'sort' && !!v)
    .map(([k]) => k);
  const bulkAdvancedFiltersActive = bulkActiveAdvancedFilterKeys.length > 0;
  const bulkActiveAdvancedFilterLabels = bulkActiveAdvancedFilterKeys
    .map(k => BULK_IGNORED_FILTER_LABELS[k]?.[lang === 'tr' ? 'tr' : 'en'] || k)
    .join(', ');

  // coordinator bulgusu (uçtan uca test): deeplink alanı eskiden SADECE
  // açık-yönlendirme/format doğrulaması yapıyordu ("/" ile başlıyor mu vb.),
  // "bu route App.js'te gerçekten var mı" kontrolü YOKTU — admin arayüzün
  // kendi (yanlış) Türkçe placeholder örneğini ("/kurslar") kullanıp
  // öğrenciye ölü link gönderdi. bulkDeeplinkKnownRoute SADECE bir uyarı
  // üretir, gönderimi ENGELLEMEZ (bkz. knownRoutes.js — admin bilinçli
  // olarak yeni/listede olmayan bir route'a da link verebilmeli).
  const bulkDeeplinkTrimmed = bulkMsg?.deeplink?.trim() || '';
  const bulkDeeplinkFormatValid = !bulkDeeplinkTrimmed || isSafeInternalDeeplink(bulkDeeplinkTrimmed);
  const bulkDeeplinkKnownRoute = !bulkDeeplinkTrimmed || isKnownRoutePath(bulkDeeplinkTrimmed);

  // Başlık+mesaj metninde bir URL/bağlantı tespit edilirse "Gönder"e basınca
  // engelleme YOK, sadece ek bir onay adımı gösterilir (bkz. import yorumu
  // üstte + utils/urlDetection.js). `deeplink` alanı zaten ayrı bir guard'dan
  // (isSafeInternalDeeplink) geçtiği için buraya dahil edilmez.
  const bulkDetectedUrl = findUrlInText(`${bulkMsg?.title || ''} ${bulkMsg?.message || ''}`);

  const bulkChannels = bulkMsg?.channels || ['in_app'];
  const bulkHasExternalChannel = bulkChannels.includes('email') || bulkChannels.includes('whatsapp');
  // effectiveCount KULLANILIR (ham count DEĞİL) — backend admin/superadmin
  // hedeflerini artık her zaman atlıyor, gösterilen sayı ve risk eşiği
  // GERÇEKTE ulaşılacak kişi sayısına dayanmalı (bkz. preview-count effect
  // yorumu). 'selected' modunda selectedIds zaten sadece öğrencilerden
  // oluşuyor (tablo /admin/student-ops'tan geliyor) — protected_count
  // kavramı o modda yok.
  const bulkTargetCount = bulkTargetMode === 'selected' ? selectedIds.length : bulkPreview.effectiveCount;
  // Reviewer bulgusu (2. tur): eşik eskiden SADECE 'all_matching' modunda
  // hesaplanıyordu — "Seçili Öğrenciler" modunda da 500'e kadar hedefe dış
  // kanaldan gönderim yapılabiliyor (aynı sıralı-gönderim/timeout riski, bkz.
  // bulk.js modül dokümantasyonu) ama zorunlu onay hiç tetiklenmiyordu. Artık
  // moddan bağımsız, ortak bulkTargetCount üzerinden uygulanıyor.
  const bulkRequiresRiskAck = bulkHasExternalChannel
    && typeof bulkTargetCount === 'number' && bulkTargetCount > BULK_EXTERNAL_RISK_THRESHOLD;

  // Reviewer bulgusu (2. tur): hedef sayısı değiştiğinde (ör. 'all_matching'
  // modunda arama metni değişip 350'den 1400'e çıkması) önceki onay ("anladım"
  // checkbox'ı) otomatik sıfırlanmalı — aksi halde admin küçük bir kitle için
  // verdiği onayla çok daha büyük bir kitleye gönderim yapabilir.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setBulkRiskAck(false); }, [bulkTargetCount]);

  // Savunma katmanı: modal AÇIKKEN (ör. arka planda filtre panelinden) bir
  // gelişmiş filtre aktif hale gelirse ve mod hâlâ 'all_matching' ise, geri
  // dönüşü olmayan yanlış-geniş bir gönderimi önlemek için modu otomatik
  // 'selected'e düşür ve admin'i bilgilendir.
  useEffect(() => {
    if (bulkMsg && bulkTargetMode === 'all_matching' && bulkAdvancedFiltersActive) {
      setBulkTargetMode('selected');
      setBulkRiskAck(false);
      toast.warning(lang === 'tr'
        ? 'Gelişmiş filtre aktif olduğu için hedef modu "Seçili Öğrenciler"e alındı.'
        : 'Switched to "Selected Students" because an advanced filter is active.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkAdvancedFiltersActive, bulkMsg, bulkTargetMode]);

  const toggleBulkChannel = (ch) => {
    if (!bulkMsg) return;
    setBulkRiskAck(false);
    setBulkMsg(m => {
      const current = m.channels || ['in_app'];
      const next = current.includes(ch) ? current.filter(c => c !== ch) : [...current, ch];
      return { ...m, channels: next.length ? next : ['in_app'] };
    });
  };

  const switchBulkTargetMode = (mode) => {
    // Gelişmiş filtre aktifken 'all_matching'e geçiş engellenir — bkz.
    // BULK_IGNORED_FILTER_LABELS yorumu üstte.
    if (mode === 'all_matching' && bulkAdvancedFiltersActive) return;
    setBulkTargetMode(mode);
    setBulkRiskAck(false);
  };

  const openBulkModal = () => {
    setBulkMsg({ title: '', message: '', channels: ['in_app'], deeplink: '' });
    setBulkPushTelemetry(null); // önceki gönderimin push telemetrisi yeni gönderime taşınmasın
    // Hiç öğrenci checkbox'la seçilmemişse VE aktif gelişmiş filtre yoksa
    // "filtreye uyan tümü" moduyla aç — aksi halde 0 hedefle açılıp Gönder
    // butonu hep kilitli kalırdı. Gelişmiş filtre aktifse bu mod devre dışı
    // olduğundan her zaman 'selected' ile aç (admin ya öğrenci seçer ya da
    // filtreyi temizleyip yeniden dener).
    setBulkTargetMode(selectedIds.length > 0 || bulkAdvancedFiltersActive ? 'selected' : 'all_matching');
    setBulkPreview({ count: null, effectiveCount: null, protectedCount: 0, loading: false, error: false, exceedsLimit: false });
    setBulkRiskAck(false);
    setBulkMessageCategory('service');
    setBulkUrlConfirmOpen(false);
  };

  // "Gönder" butonunun onClick'i — mesaj/başlıkta bir URL tespit edilirse
  // doğrudan sendBulkMessage() ÇAĞIRMAZ, önce onay dialoğu açar (bkz.
  // bulkDetectedUrl + ConfirmDialog render'ı). Tespit yoksa davranış AYNEN
  // eskisi gibi — ek tıklama yok.
  const handleBulkSendClick = () => {
    if (bulkDetectedUrl) { setBulkUrlConfirmOpen(true); return; }
    sendBulkMessage();
  };

  const bulkSendDisabled = () => {
    if (sendingMsg) return true;
    if (!bulkMsg || !bulkMsg.title?.trim() || !bulkMsg.message?.trim()) return true;
    // Format geçersizliği (open-redirect guard) gönderimi ENGELLER; bilinmeyen
    // route (bulkDeeplinkKnownRoute) sadece bir uyarı — bilinçli olarak
    // engellemiyor (bkz. bulkDeeplinkKnownRoute yorumu).
    if (!bulkDeeplinkFormatValid) return true;
    if (bulkTargetMode === 'selected' && selectedIds.length === 0) return true;
    if (bulkTargetMode === 'all_matching') {
      if (bulkAdvancedFiltersActive) return true;
      if (bulkPreview.loading || bulkPreview.error || bulkPreview.count == null) return true;
      if (bulkPreview.exceedsLimit) return true;
    }
    if (bulkRequiresRiskAck && !bulkRiskAck) return true;
    return false;
  };

  const sendBulkMessage = async () => {
    if (!bulkMsg || !bulkMsg.title?.trim() || !bulkMsg.message?.trim()) { toast.error(lang === 'tr' ? 'Baslik ve mesaj gerekli' : 'Title and message required'); return; }
    if (!bulkDeeplinkFormatValid) {
      toast.error(lang === 'tr' ? 'Bağlantı "/" ile başlayan uygulama-içi bir yol olmalı (ör. /courses)' : 'Link must be an internal path starting with "/" (e.g. /courses)');
      return;
    }
    if (bulkTargetMode === 'selected' && selectedIds.length === 0) { toast.error(lang === 'tr' ? 'En az bir öğrenci seçin' : 'Select at least one student'); return; }
    if (bulkTargetMode === 'all_matching' && (bulkPreview.count == null || bulkPreview.error)) {
      toast.error(lang === 'tr' ? 'Hedef kitle sayısı alınamadı, tekrar deneyin' : 'Could not determine target count, retry');
      return;
    }
    setSendingMsg(true);
    try {
      const basePayload = {
        title: bulkMsg.title.trim(),
        message: bulkMsg.message.trim(),
        channels: bulkMsg.channels || ['in_app'],
        ...(bulkMsg.deeplink?.trim() ? { deeplink: bulkMsg.deeplink.trim() } : {}),
        // message_category — backend/routes/admin_bulk.py::BulkNotifyPayload
        // (commit 51cf61f, doğrulandı). "service" (varsayılan): dış kanal
        // yalnızca kullanıcı opt-out ETMEMİŞSE gider. "commercial": dış kanal
        // yalnızca açık rızası (opt-in) OLAN alıcılara gider.
        message_category: bulkMessageCategory,
      };
      const payload = bulkTargetMode === 'all_matching'
        ? {
            ...basePayload,
            select_all_matching: true,
            filters: { role: 'student', search: search || undefined },
            // acknowledged_count: HAM bulkPreview.count GÖNDERİLİR — bulkPreview.
            // effectiveCount DEĞİL. Bu bilinçli bir ayrım, "tutarlılık" adına
            // birleştirilmemeli: backend'in TOCTOU guard'ı (_resolve_target_user_ids)
            // acknowledged_count'u build_admin_user_filter ile TEKRAR saydığı ham
            // (admin/superadmin dahil) sayıyla karşılaştırır — effective_count
            // gönderirsen bu karşılaştırma HER ZAMAN sapar ve her gönderim 409
            // acknowledged_count_mismatch ile başarısız olur (bkz. backend/routes/
            // admin_bulk.py::bulk_preview_count docstring, coordinator eşitleme turu).
            acknowledged_count: bulkPreview.count,
          }
        : { ...basePayload, user_ids: selectedIds };

      const r = await axios.post(`${API}/admin/bulk/notify`, payload, { headers: authHeaders() });
      const result = r.data || {};
      const summary = lang === 'tr'
        ? `Gönderildi: ${result.succeeded ?? '?'}/${result.total ?? '?'} başarılı${result.failed ? `, ${result.failed} başarısız` : ''}`
        : `Sent: ${result.succeeded ?? '?'}/${result.total ?? '?'} succeeded${result.failed ? `, ${result.failed} failed` : ''}`;
      toast.success(summary);
      // İzin yokluğundan elenen alıcı sayısı — backend/routes/admin_bulk.py
      // ::bulk_notify'daki BulkResult.skipped_no_consent alanı (commit
      // 51cf61f, doğrulandı). "service" kategoride de >0 olabilir — kullanıcı
      // hizmet bildirimlerinden opt-out etmişse (bkz. NotificationPreferences
      // Section.js) o da dış kanaldan elenir, sadece "commercial" özel değil.
      const skippedNoConsent = result.skipped_no_consent ?? 0;
      if (skippedNoConsent > 0) {
        toast.warning(lang === 'tr'
          ? `${skippedNoConsent} kişiye, iletişim tercihleri nedeniyle e-posta/WhatsApp gönderilmedi (uygulama içi bildirim yine gitti).`
          : `${skippedNoConsent} recipient(s) did not receive email/WhatsApp due to their communication preferences (the in-app notification was still sent).`);
      }
      // Push fan-out telemetrisi (backend/routes/admin_bulk.py::bulk_notify —
      // feature/bulk-push-fanout, 2026-08-04 görev talimatı). Altı alan da
      // eski response'larda YOK olabilir (backend henüz güncellenmemiş bir
      // ortamda) — `?? 0` ile hepsi güvenli varsayılana düşer, panel bu
      // durumda (hiçbir alan >0 değilken) hiç render edilmez, UI patlamaz.
      setBulkPushTelemetry({
        pushEligibleUsers: result.push_eligible_users ?? 0,
        pushSkippedNoPref: result.push_skipped_no_pref ?? 0,
        pushSkippedNoTokens: result.push_skipped_no_tokens ?? 0,
        pushSkippedCommercial: result.push_skipped_commercial ?? 0,
        pushTokensSent: result.push_tokens_sent ?? 0,
        pushTokensFailed: result.push_tokens_failed ?? 0,
      });
      setBulkMsg(null);
      setSelectedIds([]);
    } catch (err) {
      const code = getApiErrorCode(err);
      if (code === 'acknowledged_count_mismatch') {
        // Backend'in 409 detail.current_count'u HAM sayımdır (count ile aynı
        // anlam — admin/superadmin dahil, bkz. _resolve_target_user_ids).
        // Bu sayfa filters.role'ü HER ZAMAN 'student' gönderdiğinden
        // protected_count zaten hep 0'dır (admin/superadmin bu filtreyle asla
        // eşleşmez) — bu yüzden effectiveCount'u da aynı değere eşitlemek
        // güvenli; aksi halde ekranda gösterilen sayı (effectiveCount) ile
        // bir sonraki denemede gönderilecek acknowledged_count (count) senkron
        // kalmazdı.
        const newCount = err?.response?.data?.detail?.current_count;
        if (typeof newCount === 'number') setBulkPreview(p => ({ ...p, count: newCount, effectiveCount: newCount - (p.protectedCount || 0) }));
        toast.warning(lang === 'tr'
          ? 'Hedef kitle değişti, sayı güncellendi. Lütfen kontrol edip tekrar gönderin.'
          : 'Target audience changed, count refreshed. Please review and retry.');
      } else if (code === 'select_all_limit_exceeded') {
        setBulkPreview(p => ({ ...p, exceedsLimit: true }));
        toast.error(getApiErrorMessage(err, lang));
      } else {
        toast.error(getApiErrorMessage(err, lang));
      }
    }
    finally { setSendingMsg(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchStudents(); }, [search, filters, studentPage]);
  useEffect(() => {
    axios.get(`${API}/courses?limit=100`).then(r => setCourses(r.data.courses)).catch(() => {});
  }, []);

  const toggleSelect = (uid) => {
    setSelectedIds(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === students.length) setSelectedIds([]);
    else setSelectedIds(students.map(s => s.user_id));
  };

  // Toplu işlem hedefi TEK KAYNAKTAN çalışır: pencere açılırken dondurulan (ekranda görünen
  // ∩ seçili) küme. Seçim sayfa / arama / filtre değişiminde yaşadığı için `selectedIds`
  // ekranda görünmeyen kimlikler de taşır; bunlar işleme GİRMEZ, pencere kaç tanesinin
  // dışarıda kaldığını yazıyla söyler.
  const freezeVisibleSelection = () => {
    const visibleIds = new Set(students.map(s => s.user_id));
    return {
      students: students.filter(s => selectedIds.includes(s.user_id)),
      offPageCount: selectedIds.filter(id => !visibleIds.has(id)).length,
    };
  };

  // Dondurulan küme hâlâ ekrandaki listeyle ve seçimle örtüşüyor mu? Pencere açıkken liste
  // yenilendiyse (ör. havadaki sayfa isteği döndü) ya da seçim değiştiyse örtüşmez.
  const isFrozenSelectionCurrent = (ids) => {
    const visibleIds = new Set(students.map(s => s.user_id));
    return ids.length > 0 && ids.every(id => selectedIds.includes(id) && visibleIds.has(id));
  };

  // Toplu silmede pencerenin saydığı/listelediği küme de, sunucuya giden `user_ids` de
  // dondurulan diziden türetilir. Eski kod pencerede sayfadakileri sayıp isteğe
  // `selectedIds`'in tamamını koyuyordu: "2 Öğrenci Silinecek" deyip 12 siliyordu.
  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    const frozen = freezeVisibleSelection();
    if (frozen.students.length === 0) {
      // Seçimin tamamı başka sayfada: onaylanacak görünür küme yok, pencere açılmaz.
      toast.warning(lang === 'tr'
        ? `Bu sayfada seçili öğrenci yok. Başka sayfada seçili ${frozen.offPageCount} öğrenci bu işleme dahil değil.`
        : `No students selected on this page. ${frozen.offPageCount} students selected on other pages are not included.`);
      return;
    }
    setBulkDeleteModal({ students: frozen.students, offPageCount: frozen.offPageCount, confirmText: '' });
  };

  const handleBulkDeleteConfirm = async () => {
    if (!bulkDeleteModal) return;
    const expectedText = lang === 'tr' ? 'EVET' : 'YES';
    if ((bulkDeleteModal.confirmText || '').trim().toUpperCase() !== expectedText) {
      toast.warning(lang === 'tr' ? `Silmek icin "${expectedText}" yazin` : `Type "${expectedText}" to delete`);
      return;
    }
    const idsToDelete = bulkDeleteModal.students.map(s => s.user_id);
    // Onay ile silme arasında küme değiştiyse dondurulan küme artık ekrandakiyle
    // örtüşmüyor: hiçbir şey silinmez, pencere kapanır.
    if (!isFrozenSelectionCurrent(idsToDelete)) {
      toast.error(lang === 'tr'
        ? 'Liste değişti; hiçbir öğrenci silinmedi. Seçimi kontrol edip tekrar deneyin.'
        : 'The list changed; no students were deleted. Check your selection and try again.');
      setBulkDeleteModal(null);
      return;
    }
    setDeleting(true);
    try {
      const r = await axios.post(`${API}/admin/students/bulk-delete`, { user_ids: idsToDelete }, { headers: authHeaders() });
      toast.success(lang === 'tr' ? `${r.data.deleted} öğrenci silindi` : `${r.data.deleted} students deleted`);
      setBulkDeleteModal(null);
      // Yalnız silinenler seçimden düşer; başka sayfalardaki seçim yerinde kalır.
      setSelectedIds(prev => prev.filter(id => !idsToDelete.includes(id)));
      fetchStudents();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setDeleting(false); }
  };

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: lang === 'tr' ? 'Öğrenciler' : 'Students' },
          { label: lang === 'tr' ? 'Öğrenci Yönetimi' : 'Student Management' },
        ]}
        title={lang === 'tr' ? 'Öğrenci Yönetimi' : 'Student Management'}
        subtitle={lang === 'tr' ? 'Öğrenci durumu, kurslar, ödemeler ve işlemler' : 'Student status, courses, payments and operations'}
      />

      {/* Bulk Delete Confirmation Modal */}
      <AnimatePresence>
        {bulkDeleteModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
            onClick={() => !deleting && setBulkDeleteModal(null)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto shadow-2xl"
              onClick={e => e.stopPropagation()} data-testid="bulk-delete-modal">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle size={24} className="text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-red-900">
                    {lang === 'tr'
                      ? `${bulkDeleteModal.students.length} Öğrenci Silinecek`
                      : `Delete ${bulkDeleteModal.students.length} Students`}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {lang === 'tr'
                      ? 'Bu işlem GERİ ALINAMAZ. Öğrencilerin kayıtları, sertifikaları ve ödemeleri silinecek.'
                      : "This action CANNOT BE UNDONE. Students' enrollments, certificates and payments will be deleted."}
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-3 mb-4 text-sm">
                <p className="font-bold mb-2">
                  {lang === 'tr' ? 'Silinecek öğrenciler:' : 'Students to be deleted:'}
                </p>
                <ul className="space-y-1">
                  {bulkDeleteModal.students.slice(0, 5).map(s => (
                    <li key={s.user_id} className="text-gray-700">
                      &bull; {s.name || s.email} ({s.email})
                    </li>
                  ))}
                  {bulkDeleteModal.students.length > 5 && (
                    <li className="text-gray-500 italic">
                      ...{lang === 'tr'
                        ? `ve ${bulkDeleteModal.students.length - 5} daha`
                        : `and ${bulkDeleteModal.students.length - 5} more`}
                    </li>
                  )}
                </ul>
              </div>

              {bulkDeleteModal.offPageCount > 0 && (
                <p className="mb-4 rounded-xl border-2 border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-800" data-testid="bulk-delete-offpage-note">
                  {lang === 'tr'
                    ? `Başka sayfada seçili ${bulkDeleteModal.offPageCount} öğrenci bu işleme dahil değil.`
                    : `${bulkDeleteModal.offPageCount} students selected on other pages are not included.`}
                </p>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  {lang === 'tr' ? 'Silmek icin "EVET" yazin:' : 'Type "YES" to delete:'}
                </label>
                <input
                  type="text"
                  value={bulkDeleteModal.confirmText}
                  onChange={e => setBulkDeleteModal({ ...bulkDeleteModal, confirmText: e.target.value })}
                  className="w-full p-2 border-2 border-red-300 rounded-lg focus:border-red-500 focus:outline-none"
                  placeholder={lang === 'tr' ? 'EVET' : 'YES'}
                  autoFocus
                  data-testid="bulk-delete-confirm-input"
                />
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setBulkDeleteModal(null)}
                  disabled={deleting}
                  className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-700 text-sm font-bold disabled:opacity-50"
                  data-testid="bulk-delete-cancel"
                >
                  {lang === 'tr' ? 'İptal' : 'Cancel'}
                </button>
                <button
                  onClick={handleBulkDeleteConfirm}
                  disabled={deleting}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold disabled:opacity-50 hover:bg-red-700"
                  data-testid="bulk-delete-confirm"
                >
                  {deleting
                    ? (lang === 'tr' ? 'Siliniyor...' : 'Deleting...')
                    : (lang === 'tr' ? 'Sil' : 'Delete')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toplu bildirim push telemetrisi — backend/routes/admin_bulk.py::
          bulk_notify'nin push fan-out sonucu (feature/bulk-push-fanout,
          2026-08-04 görev talimatı). bulkPushTelemetry hiçbir alan >0
          değilken null'a EŞDEĞER davranır (aşağıdaki hasAnyPushSignal). */}
      {bulkPushTelemetry && (
        bulkPushTelemetry.pushTokensSent > 0
        || bulkPushTelemetry.pushTokensFailed > 0
        || bulkPushTelemetry.pushSkippedNoPref > 0
        || bulkPushTelemetry.pushSkippedNoTokens > 0
        || bulkPushTelemetry.pushSkippedCommercial > 0
      ) && (
        <div className="card-clay p-3 mb-4 border-2 border-cyan-200 bg-cyan-50" data-testid="bulk-push-telemetry">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-cyan-800 flex items-center gap-1.5">
              <Smartphone size={14} /> {lang === 'tr' ? 'Son Toplu Bildirim — Push Sonucu' : 'Last Bulk Notification — Push Result'}
            </span>
            <button onClick={() => setBulkPushTelemetry(null)} className="text-cyan-700 hover:text-cyan-900" aria-label={lang === 'tr' ? 'Kapat' : 'Close'} data-testid="bulk-push-telemetry-close">
              <XIcon size={14} />
            </button>
          </div>
          <ul className="space-y-1.5 text-[11px] text-cyan-900">
            {bulkPushTelemetry.pushTokensSent > 0 && (
              <li className="flex items-start gap-1.5">
                <CheckCircle size={12} className="text-emerald-600 shrink-0 mt-0.5" />
                {lang === 'tr' ? `${bulkPushTelemetry.pushTokensSent} cihaza push bildirimi gönderildi.` : `Push sent to ${bulkPushTelemetry.pushTokensSent} device(s).`}
              </li>
            )}
            {bulkPushTelemetry.pushTokensFailed > 0 && (
              <li className="flex items-start gap-1.5">
                <XCircle size={12} className="text-rose-600 shrink-0 mt-0.5" />
                {lang === 'tr' ? `${bulkPushTelemetry.pushTokensFailed} cihaza push gönderilemedi.` : `Push failed for ${bulkPushTelemetry.pushTokensFailed} device(s).`}
              </li>
            )}
            {bulkPushTelemetry.pushSkippedNoPref > 0 && (
              <li className="flex items-start gap-1.5">
                <AlertTriangle size={12} className="text-amber-600 shrink-0 mt-0.5" />
                {lang === 'tr'
                  ? `${bulkPushTelemetry.pushSkippedNoPref} kişi push bildirim tercihini kapattığı için push almadı (uygulama içi bildirim yine gitti).`
                  : `${bulkPushTelemetry.pushSkippedNoPref} recipient(s) did not receive push — their push preference is off (the in-app notification was still sent).`}
              </li>
            )}
            {bulkPushTelemetry.pushSkippedNoTokens > 0 && (
              <li className="flex items-start gap-1.5">
                <AlertTriangle size={12} className="text-amber-600 shrink-0 mt-0.5" />
                {lang === 'tr'
                  ? `${bulkPushTelemetry.pushSkippedNoTokens} kişinin kayıtlı aktif cihazı olmadığı için push gitmedi (uygulama içi bildirim yine gitti).`
                  : `${bulkPushTelemetry.pushSkippedNoTokens} recipient(s) have no active registered device — no push was sent to them (the in-app notification was still sent).`}
              </li>
            )}
            {bulkPushTelemetry.pushSkippedCommercial > 0 && (
              <li className="flex items-start gap-1.5 font-semibold" data-testid="bulk-push-commercial-note">
                <Info size={12} className="text-cyan-700 shrink-0 mt-0.5" />
                {lang === 'tr'
                  ? `${bulkPushTelemetry.pushSkippedCommercial} kişiye, ticari ileti kategorisinde olduğu için push gönderilmedi — bu bilinçli bir kısıt (ticari push için ayrı onay/opt-in altyapısı henüz yok), bir hata değil. Uygulama içi bildirim yine herkese gönderildi.`
                  : `${bulkPushTelemetry.pushSkippedCommercial} recipient(s) received no push because the message is categorized as commercial — this is an intentional limitation (there's no separate commercial push opt-in infrastructure yet), not an error. The in-app notification was still sent to everyone.`}
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Student List */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input value={search} onChange={e => { setSearch(e.target.value); setStudentPage(0); }} placeholder={lang === 'tr' ? 'Öğrenci ara (ad, email, TC)...' : 'Search students...'}
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none" data-testid="student-search" />
        </div>
        <button onClick={() => setShowFilters(v => !v)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border-2 transition-colors ${showFilters || activeFilterCount ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]' : 'bg-white border-[var(--border-light)] text-[var(--text-main)] hover:bg-gray-50'}`} data-testid="toggle-filters">
          <Filter size={16} /> {lang === 'tr' ? 'Filtreler' : 'Filters'}{activeFilterCount ? ` (${activeFilterCount})` : ''}
        </button>
        <button onClick={exportExcel} disabled={exporting} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-bold border-2 border-emerald-600 hover:bg-emerald-600 disabled:opacity-50 transition-colors" data-testid="export-excel-btn">
          <Download size={16} /> {exporting ? '...' : 'Excel'}
        </button>
        <button onClick={openBulkModal} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border-2 border-blue-200 text-blue-700 text-sm font-bold hover:bg-blue-50 transition-colors" data-testid="bulk-notify-toolbar-btn">
          <Mail size={16} /> {lang === 'tr' ? 'Toplu Bildirim' : 'Bulk Notify'}
        </button>
        <button onClick={() => setPushTestOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border-2 border-cyan-200 text-cyan-700 text-sm font-bold hover:bg-cyan-50 transition-colors" data-testid="push-self-test-toolbar-btn" title={lang === 'tr' ? 'Toplu bildirim göndermeden önce push zincirini kendi hesabında test edin' : 'Test the push chain on your own account before sending a bulk notification'}>
          <BellRing size={16} /> {lang === 'tr' ? 'Push Testi' : 'Push Test'}
        </button>
        <span className="text-sm text-[var(--text-muted)] font-semibold">{total}{capped ? '+' : ''} {lang === 'tr' ? 'öğrenci' : 'students'}</span>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate('/admin/users/new?role=student')}
          className="ml-auto flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-500 text-white text-sm font-bold border-2 border-cyan-600 hover:bg-cyan-600 transition-colors"
          data-testid="new-student-btn"
        >
          <UserPlus2 size={16} />
          {lang === 'tr' ? 'Yeni Öğrenci' : 'New Student'}
        </motion.button>
      </div>

      {/* Hizli gruplar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap text-xs">
        <span className="text-[var(--text-muted)] font-semibold">{lang === 'tr' ? 'Hızlı:' : 'Quick:'}</span>
        <button onClick={() => setGroup({ missing_docs: 'true' })} className="px-3 py-1.5 rounded-lg border-2 border-orange-200 bg-orange-50 text-orange-700 font-bold hover:bg-orange-100">{lang === 'tr' ? 'Belgesi eksik' : 'Missing docs'}</button>
        <button onClick={() => setGroup({ exam_taken: 'false' })} className="px-3 py-1.5 rounded-lg border-2 border-blue-200 bg-blue-50 text-blue-700 font-bold hover:bg-blue-100">{lang === 'tr' ? 'Sınava girmemiş' : 'No exam'}</button>
        <button onClick={() => setGroup({ enroll_active: 'true' })} className="px-3 py-1.5 rounded-lg border-2 border-green-200 bg-green-50 text-green-700 font-bold hover:bg-green-100">{lang === 'tr' ? 'Aktif eğitimli' : 'Active'}</button>
        <button onClick={() => setGroup({ graduate: 'true' })} className="px-3 py-1.5 rounded-lg border-2 border-purple-200 bg-purple-50 text-purple-700 font-bold hover:bg-purple-100">{lang === 'tr' ? 'Mezunlar' : 'Graduates'}</button>
        {(activeFilterCount > 0 || search) && <button onClick={clearFilters} className="px-3 py-1.5 rounded-lg border-2 border-gray-200 text-gray-600 font-bold hover:bg-gray-100 flex items-center gap-1"><XIcon size={12} /> {lang === 'tr' ? 'Temizle' : 'Clear'}</button>}
      </div>

      {showFilters && (
        <div className="card-clay p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="filter-panel">
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Şehir' : 'City'}</label>
            <input value={filters.city} onChange={e => setF('city', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Meslek' : 'Profession'}</label>
            <input value={filters.meslek} onChange={e => setF('meslek', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Durum' : 'Status'}</label>
            <select value={filters.status} onChange={e => setF('status', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold">
              <option value="">{lang === 'tr' ? 'Hepsi' : 'All'}</option>
              <option value="active">{lang === 'tr' ? 'Aktif' : 'Active'}</option>
              <option value="frozen">{lang === 'tr' ? 'Dondurulmuş' : 'Frozen'}</option>
              <option value="suspended">{lang === 'tr' ? 'Askıda' : 'Suspended'}</option>
              <option value="graduated">{lang === 'tr' ? 'Mezun' : 'Graduated'}</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Eğitim' : 'Course'}</label>
            <select value={filters.course_id} onChange={e => setF('course_id', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold">
              <option value="">{lang === 'tr' ? 'Hepsi' : 'All'}</option>
              {courses.map(c => <option key={c.course_id} value={c.course_id}>{c.title}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Kayıt (baş.)' : 'From'}</label>
            <input type="date" value={filters.date_from} onChange={e => setF('date_from', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Kayıt (bit.)' : 'To'}</label>
            <input type="date" value={filters.date_to} onChange={e => setF('date_to', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Eğitim aktif' : 'Training active'}</label>
            <select value={filters.enroll_active} onChange={e => setF('enroll_active', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold">
              <option value="">{lang === 'tr' ? 'Hepsi' : 'All'}</option>
              <option value="true">{lang === 'tr' ? 'Evet' : 'Yes'}</option>
              <option value="false">{lang === 'tr' ? 'Hayır' : 'No'}</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Sınava girdi' : 'Exam taken'}</label>
            <select value={filters.exam_taken} onChange={e => setF('exam_taken', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold">
              <option value="">{lang === 'tr' ? 'Hepsi' : 'All'}</option>
              <option value="true">{lang === 'tr' ? 'Evet' : 'Yes'}</option>
              <option value="false">{lang === 'tr' ? 'Hayır' : 'No'}</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Mezun' : 'Graduate'}</label>
            <select value={filters.graduate} onChange={e => setF('graduate', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold">
              <option value="">{lang === 'tr' ? 'Hepsi' : 'All'}</option>
              <option value="true">{lang === 'tr' ? 'Evet' : 'Yes'}</option>
              <option value="false">{lang === 'tr' ? 'Hayır' : 'No'}</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Borç Durumu' : 'Debt Status'}</label>
            <select value={filters.invoice_status} onChange={e => setF('invoice_status', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold">
              <option value="">{lang === 'tr' ? 'Hepsi' : 'All'}</option>
              <option value="none">{lang === 'tr' ? 'Yok' : 'None'}</option>
              <option value="open">{lang === 'tr' ? 'Açık borç' : 'Open'}</option>
              <option value="paid">{lang === 'tr' ? 'Ödendi' : 'Paid'}</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Sıralama' : 'Sort'}</label>
            <select value={filters.sort} onChange={e => setF('sort', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold">
              <option value="">{lang === 'tr' ? 'Tarih (yeni)' : 'Newest'}</option>
              <option value="date_asc">{lang === 'tr' ? 'Tarih (eski)' : 'Oldest'}</option>
              <option value="name_asc">{lang === 'tr' ? 'Ad (A-Z)' : 'Name A-Z'}</option>
              <option value="name_desc">{lang === 'tr' ? 'Ad (Z-A)' : 'Name Z-A'}</option>
            </select>
          </div>
        </div>
      )}

      {/* Bulk Delete Bar */}
      {selectedIds.length > 0 && (
        <div className="card-clay p-3 mb-4 bg-red-50 border-2 border-red-200 flex items-center justify-between" data-testid="bulk-delete-bar">
          <span className="text-sm font-bold text-red-700">{selectedIds.length} {lang === 'tr' ? 'öğrenci seçildi' : 'selected'}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedIds([])} className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-300 hover:bg-gray-100">{lang === 'tr' ? 'İptal' : 'Cancel'}</button>
            <button onClick={openBulkModal} className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 flex items-center gap-1" data-testid="bulk-msg-btn"><Mail size={12} /> {lang === 'tr' ? 'Toplu Mesaj' : 'Message'}</button>
            <button onClick={handleBulkDelete} disabled={deleting}
              className="px-4 py-1.5 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700 disabled:opacity-50 flex items-center gap-1" data-testid="bulk-delete-btn">
              <Trash2 size={12} /> {deleting ? '...' : (lang === 'tr' ? 'Seçilenleri Sil' : 'Delete Selected')}
            </button>
          </div>
        </div>
      )}

      {bulkMsg && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !sendingMsg && setBulkMsg(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-1">{lang === 'tr' ? 'Toplu Bildirim Gönder' : 'Send Bulk Notification'}</h3>
            <p className="text-xs text-[var(--text-muted)] mb-3">
              {lang === 'tr' ? 'Bu işlem geri alınamaz — göndermeden önce hedef kitleyi ve mesajı kontrol edin.' : 'This action cannot be undone — review the target audience and message before sending.'}
            </p>

            {/* Hedef kitle modu */}
            <div className="flex gap-2 mb-3" role="radiogroup" aria-label={lang === 'tr' ? 'Hedef kitle' : 'Target audience'}>
              <button
                type="button"
                onClick={() => switchBulkTargetMode('selected')}
                disabled={sendingMsg}
                aria-pressed={bulkTargetMode === 'selected'}
                data-testid="bulk-mode-selected"
                className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold border-2 flex items-center justify-center gap-1.5 transition-colors ${bulkTargetMode === 'selected' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-[var(--border-light)] text-[var(--text-main)] hover:bg-gray-50'}`}
              >
                <CheckCircle size={13} /> {lang === 'tr' ? `Seçili Öğrenciler (${selectedIds.length})` : `Selected Students (${selectedIds.length})`}
              </button>
              <button
                type="button"
                onClick={() => switchBulkTargetMode('all_matching')}
                disabled={sendingMsg || bulkAdvancedFiltersActive}
                aria-pressed={bulkTargetMode === 'all_matching'}
                aria-disabled={bulkAdvancedFiltersActive}
                title={bulkAdvancedFiltersActive
                  ? (lang === 'tr' ? 'Gelişmiş filtreleri temizleyin (bu mod sadece arama metni + öğrenci rolünü destekler)' : 'Clear advanced filters first (this mode only supports search text + student role)')
                  : undefined}
                data-testid="bulk-mode-all-matching"
                className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold border-2 flex items-center justify-center gap-1.5 transition-colors ${bulkAdvancedFiltersActive ? 'opacity-40 cursor-not-allowed bg-gray-50 border-[var(--border-light)] text-[var(--text-muted)]' : bulkTargetMode === 'all_matching' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-[var(--border-light)] text-[var(--text-main)] hover:bg-gray-50'}`}
              >
                <Users size={13} /> {lang === 'tr' ? 'Filtreye Uyan Tüm Öğrenciler' : 'All Matching Students'}
              </button>
            </div>

            {bulkAdvancedFiltersActive && (
              <div className="rounded-xl bg-amber-50 border-2 border-amber-300 p-2.5 mb-3 text-xs text-amber-900 flex items-start gap-2" data-testid="bulk-advanced-filters-warning">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  {lang === 'tr'
                    ? `"Filtreye Uyan Tüm Öğrenciler" modu şu an ${bulkActiveAdvancedFilterKeys.length} aktif gelişmiş filtre yüzünden KAPALI: ${bulkActiveAdvancedFilterLabels}. Bu filtreler toplu bildirim hedefine yansımaz — gördüğünüzden çok daha geniş bir kitleye gidebilir. Devam etmek için üstteki "Temizle" ile filtreleri kaldırın.`
                    : `"All Matching Students" mode is DISABLED because ${bulkActiveAdvancedFilterKeys.length} advanced filter(s) are active: ${bulkActiveAdvancedFilterLabels}. These filters don't apply to the bulk-notify target — it could reach a far larger audience than what you see. Use "Clear" above to remove them first.`}
                </span>
              </div>
            )}

            {bulkTargetMode === 'selected' && selectedIds.length === 0 && (
              <p className="text-xs text-rose-600 font-semibold mb-3">
                {lang === 'tr' ? 'Hiç öğrenci seçilmedi. Listeden öğrenci seçin ya da "Filtreye Uyan Tüm Öğrenciler" modunu kullanın.' : 'No students selected. Select students from the list or use "All Matching Students".'}
              </p>
            )}

            {bulkTargetMode === 'all_matching' && !bulkAdvancedFiltersActive && (
              <div className="rounded-xl bg-cyan-50 border-2 border-cyan-200 p-2.5 mb-3 text-xs" data-testid="bulk-preview-count">
                <p className="font-bold text-cyan-800 flex items-center gap-1.5">
                  {bulkPreview.loading && <Loader2 size={12} className="animate-spin" />}
                  {bulkPreview.loading
                    ? (lang === 'tr' ? 'Hesaplanıyor...' : 'Calculating...')
                    : bulkPreview.error
                      ? (lang === 'tr' ? 'Sayım alınamadı' : 'Could not fetch count')
                      /* effectiveCount gösterilir (ham count DEĞİL) — bkz.
                         preview-count effect yorumu üstte. */
                      : (lang === 'tr' ? `${bulkPreview.effectiveCount ?? '—'} öğrenciye gönderilecek` : `Will be sent to ${bulkPreview.effectiveCount ?? '—'} students`)}
                </p>
                {!bulkPreview.loading && !bulkPreview.error && bulkPreview.protectedCount > 0 && (
                  <p className="text-[11px] text-cyan-700 font-semibold mt-1" data-testid="bulk-protected-count-note">
                    {lang === 'tr'
                      ? `(${bulkPreview.protectedCount} yönetici hesabı hariç tutuldu — toplam ${bulkPreview.count} kişi filtreye uyuyor.)`
                      : `(${bulkPreview.protectedCount} admin account(s) excluded — ${bulkPreview.count} total match the filter.)`}
                  </p>
                )}
                <p className="text-[11px] text-cyan-700 mt-1">
                  {lang === 'tr'
                    ? 'Not: Bu modda yalnızca yukarıdaki arama kutusuna girilen metin + öğrenci rolü dikkate alınır; şehir/meslek/durum gibi diğer gelişmiş filtreler bu gönderime dahil edilmez.'
                    : 'Note: Only the search box text above + student role apply in this mode; other advanced filters (city/occupation/status) are not included.'}
                </p>
                {bulkPreview.exceedsLimit && (
                  <p className="text-rose-600 font-bold mt-1">
                    {lang === 'tr' ? 'Hedef kitle 5000 kaydı aşıyor — arama metnini daraltın.' : 'Target audience exceeds 5000 records — narrow the search.'}
                  </p>
                )}
              </div>
            )}

            {/* İleti türü (KVKK) — bkz. bulkMessageCategory state yorumu üstte:
                planlanan backend şeması, bu görev anında henüz doğrulanamadı. */}
            <div className="mb-3">
              <span className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">{lang === 'tr' ? 'İleti Türü' : 'Message Type'}</span>
              <div className="flex gap-2" role="radiogroup" aria-label={lang === 'tr' ? 'İleti türü' : 'Message type'}>
                <button
                  type="button"
                  onClick={() => setBulkMessageCategory('service')}
                  disabled={sendingMsg}
                  aria-pressed={bulkMessageCategory === 'service'}
                  data-testid="bulk-category-service"
                  className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold border-2 flex items-center justify-center gap-1.5 transition-colors ${bulkMessageCategory === 'service' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-[var(--border-light)] text-[var(--text-main)] hover:bg-gray-50'}`}
                >
                  <ShieldCheck size={13} /> {lang === 'tr' ? 'Hizmet' : 'Service'}
                </button>
                <button
                  type="button"
                  onClick={() => setBulkMessageCategory('commercial')}
                  disabled={sendingMsg}
                  aria-pressed={bulkMessageCategory === 'commercial'}
                  data-testid="bulk-category-commercial"
                  className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold border-2 flex items-center justify-center gap-1.5 transition-colors ${bulkMessageCategory === 'commercial' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white border-[var(--border-light)] text-[var(--text-main)] hover:bg-gray-50'}`}
                >
                  <Megaphone size={13} /> {lang === 'tr' ? 'Ticari' : 'Commercial'}
                </button>
              </div>
              <p className="text-[11px] text-[var(--text-muted)] mt-1">
                {lang === 'tr'
                  ? 'Hizmet: ders hatırlatmaları, mülakat sonuçları gibi eğitim sürecine dair mesajlar — kullanıcı hizmet bildirimlerini kapatmadıysa dış kanaldan da gider. Ticari: kampanya/duyuru gibi tanıtım mesajları — yalnızca açık rızası (izni) olan alıcılara dış kanaldan gider.'
                  : "Service: course-related messages like lesson reminders and interview results — external channels are used unless the recipient turned service notifications off. Commercial: promotional messages like campaigns/announcements — external channels only reach recipients who've given explicit consent."}
              </p>
            </div>

            {bulkMessageCategory === 'commercial' && (
              <div className="rounded-xl bg-amber-50 border-2 border-amber-300 p-2.5 mb-3 text-xs text-amber-900 flex items-start gap-2" data-testid="bulk-commercial-consent-warning">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  {lang === 'tr'
                    ? 'Ticari ileti seçildi. E-posta/WhatsApp yalnızca bu kanallar için açık rızası olan alıcılara gider — rızası olmayanlar otomatik olarak atlanır (uygulama içi bildirim yine herkese gider). "Gönderdim" saydığınız kişi sayısı ile fiilen ulaşan sayı FARKLI olabilir; gönderim sonrası kaç kişinin atlandığını göreceksiniz.'
                    : "Commercial message selected. Email/WhatsApp will only reach recipients who've given explicit consent for that channel — others are automatically skipped (the in-app notification still reaches everyone). The number you think you \"sent to\" may differ from who actually received it; you'll see how many were skipped after sending."}
                </span>
              </div>
            )}

            {/* maxLength — backend/routes/admin_bulk.py::BulkNotifyPayload
                (Security audit YÜKSEK bulgu #1, 2026-08 turu): title
                max_length=200, message max_length=2000. Sınır aşılırsa
                backend zaten kabul etmez, burada input seviyesinde de
                aynı sınırı uyguluyoruz — PushSelfTestModal.jsx'teki
                test-self formu ile aynı desenin bulk-notify karşılığı. */}
            <input value={bulkMsg.title} onChange={e => setBulkMsg({ ...bulkMsg, title: e.target.value })} placeholder={lang === 'tr' ? 'Başlık' : 'Title'} maxLength={200} disabled={sendingMsg} className="w-full px-3 py-2 mb-2 rounded-xl border-2 border-[var(--border-light)] text-sm font-semibold disabled:opacity-60" data-testid="bulk-msg-title" />
            <textarea value={bulkMsg.message} onChange={e => setBulkMsg({ ...bulkMsg, message: e.target.value })} placeholder={lang === 'tr' ? 'Mesaj...' : 'Message...'} rows={4} maxLength={2000} disabled={sendingMsg} className="w-full px-3 py-2 mb-2 rounded-xl border-2 border-[var(--border-light)] text-sm disabled:opacity-60" data-testid="bulk-msg-text" />

            <div className="mb-3">
              <div className="relative">
                <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  value={bulkMsg.deeplink || ''}
                  onChange={e => setBulkMsg({ ...bulkMsg, deeplink: e.target.value })}
                  placeholder={lang === 'tr' ? 'Bağlantı ekle (opsiyonel, ör. /courses)' : 'Add link (optional, e.g. /courses)'}
                  disabled={sendingMsg}
                  className={`w-full pl-8 pr-3 py-2 rounded-xl border-2 text-xs font-semibold disabled:opacity-60 ${bulkDeeplinkTrimmed && !bulkDeeplinkFormatValid ? 'border-rose-400' : bulkDeeplinkTrimmed && !bulkDeeplinkKnownRoute ? 'border-amber-400' : 'border-[var(--border-light)]'}`}
                  data-testid="bulk-msg-deeplink"
                />
              </div>
              {bulkDeeplinkTrimmed && !bulkDeeplinkFormatValid ? (
                <p className="text-[11px] text-rose-600 font-semibold mt-1" data-testid="bulk-deeplink-format-error">
                  {lang === 'tr' ? '"/" ile başlayan uygulama-içi bir yol olmalı — dış bağlantı (http:, https:) veya "//" ile başlayan yollar kabul edilmez.' : 'Must be an internal path starting with "/" — external links (http:, https:) or paths starting with "//" are rejected.'}
                </p>
              ) : bulkDeeplinkTrimmed && !bulkDeeplinkKnownRoute ? (
                <p className="text-[11px] text-amber-700 font-semibold mt-1 flex items-start gap-1" data-testid="bulk-deeplink-unknown-route-warning">
                  <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                  {lang === 'tr'
                    ? 'Bu adres bilinen sayfalarla eşleşmiyor — bildirime tıklayan öğrenci ana sayfaya düşebilir. Gönderime devam edilebilir, adresi kontrol etmeniz önerilir.'
                    : "This address doesn't match any known page — a student clicking the notification may land on the homepage. You can still send, but double-check the address."}
                </p>
              ) : (
                <p className="text-[11px] text-[var(--text-muted)] mt-1">
                  {lang === 'tr' ? 'Verilirse bildirimde sabit "Detayları Gör" bağlantısı gösterilir.' : 'If set, a fixed "View Details" link is shown on the notification.'}
                </p>
              )}
            </div>

            {/* Kanallar */}
            <div className="mb-2">
              <span className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">{lang === 'tr' ? 'Kanallar' : 'Channels'}</span>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-1.5 text-xs font-semibold text-[var(--text-muted)]">
                  <input type="checkbox" checked disabled className="rounded" />
                  {lang === 'tr' ? 'Uygulama içi (zorunlu)' : 'In-app (required)'}
                </label>
                <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer">
                  <input type="checkbox" checked={bulkChannels.includes('email')} onChange={() => toggleBulkChannel('email')} disabled={sendingMsg} className="rounded" data-testid="bulk-channel-email" />
                  {lang === 'tr' ? 'E-posta' : 'Email'}
                </label>
                <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer">
                  <input type="checkbox" checked={bulkChannels.includes('whatsapp')} onChange={() => toggleBulkChannel('whatsapp')} disabled={sendingMsg} className="rounded" data-testid="bulk-channel-whatsapp" />
                  {lang === 'tr' ? 'WhatsApp' : 'WhatsApp'}
                </label>
              </div>
            </div>

            {bulkHasExternalChannel && (
              <div className="rounded-xl bg-amber-50 border-2 border-amber-200 p-2.5 mb-2 text-xs text-amber-800 flex items-start gap-2" data-testid="bulk-external-channel-warning">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  {lang === 'tr'
                    ? 'Dış kanal (e-posta/WhatsApp) seçili. Bu istek sunucuda sırayla işlenir — büyük hedef kitlelerde birkaç dakika sürebilir ve tarayıcı isteği zaman aşımına uğrayabilir. İşlem sürerken bu pencereyi kapatmayın.'
                    : 'External channel (email/WhatsApp) selected. This request is processed sequentially on the server — it may take several minutes for large audiences and the browser request may time out. Do not close this window while it is running.'}
                </span>
              </div>
            )}

            {bulkRequiresRiskAck && (
              <label className="flex items-start gap-2 mb-3 text-xs bg-rose-50 border-2 border-rose-200 rounded-xl p-2.5 cursor-pointer" data-testid="bulk-risk-ack">
                <input type="checkbox" checked={bulkRiskAck} onChange={e => setBulkRiskAck(e.target.checked)} disabled={sendingMsg} className="mt-0.5 rounded" />
                <span className="text-rose-700 font-semibold">
                  {/* bulkTargetCount (effectiveCount tabanlı) — risk eşiği de
                      bu sayı üzerinden tetiklendiği için metindeki sayı da
                      onunla tutarlı olmalı, ham count DEĞİL. */}
                  {lang === 'tr'
                    ? `${bulkTargetCount} kişilik büyük bir kitleye dış kanal üzerinden gönderim yapacağımı, işlemin uzun sürebileceğini ve bu işlemin geri alınamayacağını anlıyorum.`
                    : `I understand I'm sending to a large audience of ${bulkTargetCount} via an external channel, this may take a while, and this action cannot be undone.`}
                </span>
              </label>
            )}

            <div className="flex items-center justify-between gap-2 mt-1">
              <span className="text-xs font-bold text-[var(--text-muted)]">
                {typeof bulkTargetCount === 'number' ? (lang === 'tr' ? `Toplam: ${bulkTargetCount}` : `Total: ${bulkTargetCount}`) : ''}
              </span>
              <div className="flex justify-end gap-2">
                <button onClick={() => setBulkMsg(null)} disabled={sendingMsg} className="px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-100 disabled:opacity-50">{lang === 'tr' ? 'İptal' : 'Cancel'}</button>
                <button onClick={handleBulkSendClick} disabled={bulkSendDisabled()} className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5" data-testid="bulk-msg-send">
                  {sendingMsg ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {sendingMsg ? (lang === 'tr' ? 'Gönderiliyor... (uzun sürebilir)' : 'Sending... (may take a while)') : (lang === 'tr' ? 'Gönder' : 'Send')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* URL onay uyarısı — security denetimi (2026-08-04): serbest başlık/
          mesaj metni isSafeInternalDeeplink guard'ından geçmez, bu yüzden
          Gönder'e basılınca bulkDetectedUrl varsa buraya düşülür. Tek tıkla
          geçilebilir ("Yine de Gönder") — admin'i cezalandırmaz, sadece
          farkındalık sağlar (bkz. utils/urlDetection.js). */}
      <ConfirmDialog
        open={bulkUrlConfirmOpen}
        onOpenChange={(o) => { if (!o) setBulkUrlConfirmOpen(false); }}
        variant="warning"
        title={lang === 'tr' ? 'Mesajınızda bir bağlantı var' : 'Your message contains a link'}
        description={
          lang === 'tr'
            ? `Mesajınızda şu bağlantı/adres tespit edildi: "${bulkDetectedUrl}". Bazı cihazlarda bildirim metnindeki bağlantılar otomatik tıklanabilir hale gelir — göndermek istediğinize emin misiniz?`
            : `A link/address was detected in your message: "${bulkDetectedUrl}". On some devices, links in notification text become automatically clickable — are you sure you want to send it?`
        }
        confirmLabel={lang === 'tr' ? 'Yine de Gönder' : 'Send Anyway'}
        cancelLabel={lang === 'tr' ? 'Vazgeç' : 'Cancel'}
        onConfirm={() => { setBulkUrlConfirmOpen(false); sendBulkMessage(); }}
      />

      {loadingList && (
        <div className="flex items-center justify-center gap-2 py-2 mb-2 text-sm text-[var(--text-muted)] font-semibold" data-testid="students-loading">
          <RefreshCw size={14} className="animate-spin" /> {lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}
        </div>
      )}

      <div className={`card-clay overflow-hidden transition-opacity ${loadingList ? 'opacity-60' : ''}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-3 w-10"><input type="checkbox" checked={selectedIds.length === students.length && students.length > 0} onChange={toggleSelectAll} className="rounded" data-testid="select-all-students" /></th>
                <th className="text-left p-3 font-bold">{lang === 'tr' ? 'Öğrenci' : 'Student'}</th>
                <th className="text-left p-3 font-bold">{lang === 'tr' ? 'İletişim' : 'Contact'}</th>
                <th className="text-center p-3 font-bold">{lang === 'tr' ? 'Durum' : 'Status'}</th>
                <th className="text-center p-3 font-bold">{lang === 'tr' ? 'Kurs' : 'Courses'}</th>
                <th className="text-center p-3 font-bold">XP</th>
                <th className="text-right p-3 font-bold">{lang === 'tr' ? 'Harcama' : 'Spent'}</th>
                <th className="text-center p-3 font-bold">{lang === 'tr' ? 'İşlem' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s, i) => {
                const st = statusConfig[s.status] || statusConfig.active;
                return (
                  <motion.tr key={s.user_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                    className={`border-t border-[var(--border-light)] hover:bg-gray-50 cursor-pointer ${selectedIds.includes(s.user_id) ? 'bg-red-50' : ''}`} data-testid={`student-row-${i}`}>
                    <td className="p-3" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.includes(s.user_id)} onChange={() => toggleSelect(s.user_id)} className="rounded" />
                    </td>
                    <td className="p-3" onClick={() => navigate(`/admin/students/${s.user_id}`)}>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[var(--brand-primary)] flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-bold">{s.name?.[0]}</span>
                        </div>
                        <div><p className="font-bold text-xs">{s.name}</p><p className="text-[10px] text-[var(--text-muted)]">{s.tc_no || '-'}</p></div>
                      </div>
                    </td>
                    <td className="p-3" onClick={() => navigate(`/admin/students/${s.user_id}`)}><p className="text-xs">{s.email}</p><p className="text-[10px] text-[var(--text-muted)]">{s.phone || '-'}</p></td>
                    <td className="p-3 text-center" onClick={() => navigate(`/admin/students/${s.user_id}`)}><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${st.color}`}>{lang === 'tr' ? st.label_tr : st.label}</span></td>
                    <td className="p-3 text-center font-bold text-xs" onClick={() => navigate(`/admin/students/${s.user_id}`)}>{s.enrollments || 0}</td>
                    <td className="p-3 text-center font-bold text-xs text-[var(--brand-secondary-shadow)]" onClick={() => navigate(`/admin/students/${s.user_id}`)}>{s.xp || 0}</td>
                    <td className="p-3 text-right font-bold text-xs text-[var(--brand-success)]" onClick={() => navigate(`/admin/students/${s.user_id}`)}>{(s.total_spent || 0).toFixed(0)} TRY</td>
                    <td className="p-3 text-center">
                      <button onClick={() => navigate(`/admin/students/${s.user_id}`)} className="p-1.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100" data-testid={`view-detail-${i}`}><Eye size={14} /></button>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination (sunucu tarafi) */}
        <div className="flex items-center justify-between p-4 border-t border-[var(--border-light)]">
          <span className="text-sm text-[var(--text-muted)] font-semibold">{total}{capped ? '+' : ''} {lang === 'tr' ? 'öğrenci' : 'students'}</span>
          <div className="flex gap-2 items-center">
            <button onClick={() => setStudentPage(Math.max(0, studentPage - 1))} disabled={studentPage === 0 || loadingList}
              className="p-2 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40" data-testid="prev-page-btn">
              <ChevronLeft size={16} />
            </button>
            <span className="px-3 py-2 text-sm font-bold">{studentPage + 1} / {totalPages}</span>
            <button onClick={() => setStudentPage(studentPage + 1)} disabled={studentPage + 1 >= totalPages || loadingList}
              className="p-2 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40" data-testid="next-page-btn">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <PushSelfTestModal open={pushTestOpen} onOpenChange={setPushTestOpen} />

    </AdminLayout>
  );
}
